/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.encodeToString
import kotlinx.serialization.decodeFromString
import org.w3c.fetch.*
import repository.ContextApiModel
import repository.ContextListResponse
import repository.CreateContextRequest
import repository.UpdateContextRequest
import repository.DeleteContextResponse
import repository.SetTransactionContextsRequest
import repository.BatchAssignContextsRequest
import repository.BatchAssignResponse
import i18n.tr

/** IANA timezone string from the browser, e.g. "America/Toronto". UTC on failure. */
internal fun browserTimezone(): String = try {
    val tz = js("Intl.DateTimeFormat().resolvedOptions().timeZone") as? String
    if (tz.isNullOrBlank()) "UTC" else tz
} catch (_: Throwable) {
    "UTC"
}

@Serializable
data class LinkTokenResponse(
    val link_token: String,
    val expiration: String
)

@Serializable
data class ExchangeTokenRequest(
    val public_token: String,
    val user_id: String,
    val days_requested: Int? = null,
    val institution_id: String? = null,
    val institution_name: String? = null
)

@Serializable
data class ExchangeTokenResponse(
    val item_id: String,
    val institution_id: String? = null,
    val status: String,
    val message: String
)

@Serializable
data class TransactionsRequest(
    val access_token: String,
    val start_date: String,
    val end_date: String,
    val count: Int = 100,
    val offset: Int = 0,
    val account_ids: List<String>? = null
)


@Serializable
data class PlaidAccount(
    val account_id: String,
    val name: String,
    val official_name: String? = null,
    val type: String,
    val subtype: String? = null,
    val mask: String? = null,
    val balance_available: Double? = null,
    val balance_current: Double? = null,
    val balance_limit: Double? = null,
    val institution_name: String? = null,
    val needs_relink: Boolean = false,
    val item_id: String? = null,
    val last_transaction_date: String? = null,
    val stale_days: Int? = null,
    // ISO-8601 (or SQLite "YYYY-MM-DD HH:MM:SS" UTC) — stamped by the server
    // when the user explicitly reconnects via Plaid Link's update flow. Used
    // to render the Plaid-stuck-after-relink message instead of the generic
    // stale badge for ~48h after the relink.
    val last_relinked_at: String? = null,
    val custom_name: String? = null,
)

@Serializable
data class PersonalFinanceCategory(
    val primary: String,
    val detailed: String,
    val confidence_level: String? = null
)

@Serializable
data class PlaidTransaction(
    val transaction_id: String,
    val account_id: String,
    val amount: Double,
    val date: String,
    val name: String,
    val merchant_name: String? = null,
    val category: List<String>? = null,
    val personal_finance_category: PersonalFinanceCategory? = null,
    val category_source: String? = null, // "pattern", "cache", "plaid", "llm", "plaid_fallback"
    // Split/Distribute transaction fields
    val parent_transaction_id: String? = null,
    val child_type: String? = null, // "SPLIT" or "DISTRIBUTED"
    val is_split_parent: Boolean = false,
    val is_distributed_parent: Boolean = false,
    val split_order: Int? = null,
    val distribution_start_month: String? = null,
    val distribution_end_month: String? = null,
    val distribution_month_index: Int? = null,
    val distribution_total_months: Int? = null,
    // Goal assignment fields
    val assigned_goal_id: String? = null,
    val assigned_goal_name: String? = null,
    val assigned_goal_icon: String? = null,
    val assigned_goal_amount: Double? = null,
    // Every goal this transaction feeds, with each goal's own share. The three
    // fields above are the lossy single-goal view (largest goal, TOTAL amount).
    val assigned_goals: List<models.AssignedGoalShare> = emptyList(),
    // Category verification fields
    val is_verified: Boolean = false,
    val verified_at: String? = null,
    val confidence_level: String? = null,
    // User notes
    val user_notes: String? = null,
    // User-set category override — when set, takes precedence over Plaid's
    // personal_finance_category for grouping/display. Format is "Primary/Detailed"
    // (we split on "/" and use the first part for top-level grouping).
    val user_category: String? = null,
    // Exclude flag
    val is_excluded: Boolean = false,
    // Hidden flag (e.g. pending auth-hold transactions paired with a posted card payment)
    val is_hidden: Boolean = false,
    // Subscription status
    val is_subscription: Boolean = false,
    // Context assignment
    val context_ids: List<String> = emptyList()
)

@Serializable
data class VerifyCategoryResponse(
    val status: String,
    val message: String,
    val propagated_count: Int = 0
)

@Serializable
data class TransactionsResponse(
    val accounts: List<PlaidAccount>,
    val transactions: List<PlaidTransaction>,
    val needs_relink: Boolean = false,
    val data_source: String = "plaid",
    val last_sync_time: String? = null,
    /** Smoothed monthly income (annual estimate ÷ 12) — denominator for
     *  the dashboard Savings Trend chart. See server's
     *  computeSmoothedMonthlyIncome. Using a smoothed value avoids the
     *  noise from biweekly paychecks creating 2-vs-3-paycheck months. */
    val estimated_monthly_income: Double? = null,
)

@Serializable
data class ApiError(
    val error: String,
    val message: String,
    val error_code: String? = null,
    val error_type: String? = null
)

@Serializable
data class RegisterRequest(
    val username: String,
    val password: String,
    val email: String? = null
)

@Serializable
data class ResendVerificationRequest(
    val email: String
)

@Serializable
data class RegisterResponse(
    val userId: String,
    val username: String,
    val message: String
)

@Serializable
data class LoginRequest(
    val username: String,
    val password: String
)

@Serializable
data class AuthTokenResponse(
    val token: String,
    val tokenType: String,
    val expiresAtEpochSeconds: Long,
    val userId: String,
    val username: String,
    val emailVerified: Boolean = true
)

@Serializable
data class PlaidLinkStatusResponse(
    val has_plaid_token: Boolean,
    val days_requested: Int? = null,
    val institution_name: String? = null
)

// User Settings API models
@Serializable
data class UserSettingRequest(
    val key: String,
    val value: String
)

@Serializable
data class UserSettingResponse(
    val key: String,
    val value: String
)

@Serializable
data class UserSettingsListResponse(
    val settings: List<UserSettingResponse>
)

@Serializable
data class GetSettingResponse(
    val key: String,
    val value: String?
)

@Serializable
data class ForgotPasswordRequest(
    val email: String,
)

@Serializable
data class ResetPasswordRequest(
    val token: String,
    val newPassword: String,
)

@Serializable
data class SubscriptionStatusData(
    val status: String,
    val subscriptionId: String? = null,
    val trialEndsAt: String? = null,
    val tier: String = "standard",
)

@Serializable
data class LinkedAccountInfo(
    val account_id: String,
    val name: String,
    val type: String,
    val subtype: String = ""
)

@Serializable
data class LinkedInstitution(
    val item_id: String,
    val institution_name: String,
    val institution_id: String = "",
    val accounts: List<LinkedAccountInfo> = emptyList(),
    val needs_relink: Boolean = false,
)

class ApiService {
    companion object {
        private var sharedAuthToken: String? = null
        private var sharedUserId: String? = null
        private var sharedTokenExpiresAt: Long = 0
        private var onSessionExpired: (() -> Unit)? = null

        fun getSharedUserId(): String? = sharedUserId

        fun getSharedAuthToken(): String? = sharedAuthToken

        // Clears the JVM/JS-wide auth state. Used by WebNavigationManager.logout()
        // so isAuthenticated() reads false on the next render — preventing the
        // logged-in landing CTAs from re-appearing after sign-out.
        fun clearSharedSession() {
            sharedAuthToken = null
            sharedUserId = null
            sharedTokenExpiresAt = 0
        }

        fun setSessionExpiredCallback(callback: () -> Unit) {
            onSessionExpired = callback
        }

        fun notifySessionExpired() {
            onSessionExpired?.invoke()
        }
    }

    private val baseUrl = if (window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1") {
        "http://localhost:3000"
    } else {
        "" // Empty = same origin, nginx will proxy /api/* to backend
    }
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }
    private var authToken: String?
        get() = sharedAuthToken
        set(value) {
            sharedAuthToken = value
        }
    private var authenticatedUserId: String?
        get() = sharedUserId
        set(value) {
            sharedUserId = value
        }
    private var tokenExpiresAt: Long
        get() = sharedTokenExpiresAt
        set(value) {
            sharedTokenExpiresAt = value
        }

    // Refresh threshold: 5 minutes before expiry
    private val refreshThresholdSeconds = 5 * 60

    fun isAuthenticated(): Boolean = authToken != null && authenticatedUserId != null

    fun clearSession() {
        authToken = null
        authenticatedUserId = null
        tokenExpiresAt = 0
    }

    fun restoreSession(token: String, userId: String, expiresAt: Long = 0) {
        authToken = token
        authenticatedUserId = userId
        tokenExpiresAt = expiresAt
    }

    /**
     * Check if the token is about to expire (within 5 minutes) and refresh it if needed.
     * Returns true if token is valid (refreshed or still good), false if session has expired.
     */
    suspend fun checkAndRefreshTokenIfNeeded(): Boolean {
        if (authToken == null) return false

        val currentTime = (kotlin.js.Date.now() / 1000).toLong()
        val timeUntilExpiry = tokenExpiresAt - currentTime

        // If token expires in more than 5 minutes, we're good
        if (timeUntilExpiry > refreshThresholdSeconds) {
            return true
        }

        // Token is about to expire or already expired - try to refresh
        console.log("Token expires in ${timeUntilExpiry}s, attempting refresh...")

        return try {
            val refreshResponse = refreshToken()
            console.log("Token refreshed successfully, new expiry: ${refreshResponse.expiresAtEpochSeconds}")
            true
        } catch (e: Exception) {
            console.error("Token refresh failed: ${e.message}")
            // If refresh fails, the session has expired
            notifySessionExpired("Your session has expired. Please log in again.")
            false
        }
    }

    /**
     * Check if token needs refresh and refresh it if needed.
     * Returns the new AuthTokenResponse if a refresh was performed, null if no refresh was needed.
     * Throws SessionExpiredException if the token has expired and cannot be refreshed.
     */
    suspend fun refreshTokenIfNeeded(): AuthTokenResponse? {
        if (authToken == null) throw SessionExpiredException("No token available")

        val currentTime = (kotlin.js.Date.now() / 1000).toLong()
        val timeUntilExpiry = tokenExpiresAt - currentTime

        // If token expires in more than 5 minutes, no refresh needed
        if (timeUntilExpiry > refreshThresholdSeconds) {
            return null
        }

        // Token is about to expire or already expired - try to refresh
        console.log("Token expires in ${timeUntilExpiry}s, attempting refresh...")

        return try {
            val response = refreshToken()
            console.log("Token refreshed successfully, new expiry: ${response.expiresAtEpochSeconds}")
            response
        } catch (e: SessionExpiredException) {
            throw e
        } catch (e: Exception) {
            console.error("Token refresh failed: ${e.message}")
            throw SessionExpiredException("Failed to refresh token: ${e.message}")
        }
    }

    /**
     * Refresh the authentication token.
     * Throws exception if refresh fails (token invalid or expired).
     */
    suspend fun refreshToken(): AuthTokenResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        val response = window.fetch("$baseUrl/api/refresh", options).await()

        if (response.status.toInt() == 401) {
            throw SessionExpiredException("Session has expired")
        }

        if (!response.ok) {
            throw Exception("Failed to refresh token: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val tokenResponse = json.decodeFromString<AuthTokenResponse>(text)

        // Update stored token
        authToken = tokenResponse.token
        authenticatedUserId = tokenResponse.userId
        tokenExpiresAt = tokenResponse.expiresAtEpochSeconds

        // Also update localStorage (will be done by WebApp after this call)
        return tokenResponse
    }

    private fun notifySessionExpired(message: String) {
        console.log("Session expired: $message")
        clearSession()
        onSessionExpired?.invoke()
    }

    private fun authHeaders(): dynamic {
        val headers = js("{}")
        headers["Content-Type"] = "application/json"
        authToken?.let { headers["Authorization"] = "Bearer $it" }
        headers["X-Wiz3r-Tz"] = browserTimezone()
        return headers
    }

    /**
     * Check any API response for 401 and trigger session expiry globally.
     * Call after every authenticated fetch to handle expired tokens uniformly.
     */
    private fun checkAuth(response: org.w3c.fetch.Response): org.w3c.fetch.Response {
        if (response.status.toInt() == 401) {
            notifySessionExpired("API returned 401")
            throw Exception("AUTH_REQUIRED")
        }
        return response
    }

    suspend fun register(username: String, password: String, email: String? = null): RegisterResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(RegisterRequest(username = username, password = password, email = email))

        val response = window.fetch("$baseUrl/api/register", options).await()
        if (response.status.toInt() != 201) {
            throw Exception("Failed to register user: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<RegisterResponse>(text)
    }

    /**
     * Login with username and password.
     * May throw MfaRequiredException if MFA is enabled for the user.
     */
    suspend fun login(username: String, password: String): AuthTokenResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(LoginRequest(username = username, password = password))

        val response = window.fetch("$baseUrl/api/login", options).await()
        if (!response.ok) {
            // 401 from /api/login means wrong email or password — surface a friendly
            // localized message instead of "Failed to login: 401 Unauthorized".
            if (response.status.toInt() == 401) {
                throw Exception(i18n.StringKey.ERROR_INVALID_CREDENTIALS.tr())
            }
            throw Exception(i18n.StringKey.ERROR_AUTH_FAILED.tr())
        }

        val text = response.text().await()

        // Check if MFA is required
        if (text.contains("\"requiresMfa\":true") || text.contains("\"requiresMfa\": true")) {
            val mfaResponse = json.decodeFromString<MfaRequiredResponse>(text)
            throw MfaRequiredException(mfaResponse.tempToken, mfaResponse.userId)
        }

        val tokenResponse = json.decodeFromString<AuthTokenResponse>(text)
        authToken = tokenResponse.token
        authenticatedUserId = tokenResponse.userId
        tokenExpiresAt = tokenResponse.expiresAtEpochSeconds
        return tokenResponse
    }

    /**
     * Start a no-account demo session. Hits the public /api/demo/session
     * endpoint (no credentials) and receives a read-only token for the shared
     * demo account. The server rejects every write carried by this token, so
     * the demo is browse-only; the client makes changes feel real with
     * local-only updates that a reload discards.
     */
    suspend fun startDemoSession(): AuthTokenResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        val response = window.fetch("$baseUrl/api/demo/session", options).await()
        if (!response.ok) {
            throw Exception(i18n.StringKey.ERROR_AUTH_FAILED.tr())
        }

        val text = response.text().await()
        val tokenResponse = json.decodeFromString<AuthTokenResponse>(text)
        authToken = tokenResponse.token
        authenticatedUserId = tokenResponse.userId
        tokenExpiresAt = tokenResponse.expiresAtEpochSeconds
        return tokenResponse
    }

    /**
     * Complete MFA verification during login.
     */
    suspend fun verifyMfaLogin(tempToken: String, code: String): AuthTokenResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(MfaLoginVerifyRequest(tempToken = tempToken, code = code))

        val response = window.fetch("$baseUrl/api/login/mfa", options).await()
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("MFA verification failed: $errorText")
        }

        val text = response.text().await()
        val tokenResponse = json.decodeFromString<AuthTokenResponse>(text)
        authToken = tokenResponse.token
        authenticatedUserId = tokenResponse.userId
        tokenExpiresAt = tokenResponse.expiresAtEpochSeconds
        return tokenResponse
    }

    // MFA Management Methods

    /**
     * Check if MFA is enabled and available for the current user.
     */
    suspend fun getMfaStatus(): MfaStatusResponse {
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/mfa/status", options).await())
        if (!response.ok) {
            throw Exception("Failed to get MFA status: ${response.status}")
        }

        val text = response.text().await()
        return json.decodeFromString<MfaStatusResponse>(text)
    }

    /**
     * Initiate MFA setup. Returns secret and backup codes.
     */
    suspend fun setupMfa(): MfaSetupResponse {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/mfa/setup", options).await())
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to setup MFA: $errorText")
        }

        val text = response.text().await()
        return json.decodeFromString<MfaSetupResponse>(text)
    }

    /**
     * Verify and enable MFA after setup.
     */
    suspend fun verifyMfaSetup(code: String, backupCodes: List<String>) {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(MfaVerifyRequest(code = code, backupCodes = backupCodes))

        val response = checkAuth(window.fetch("$baseUrl/api/mfa/verify-setup", options).await())
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to verify MFA setup: $errorText")
        }
    }

    /**
     * Disable MFA (requires valid TOTP code).
     */
    suspend fun disableMfa(code: String) {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(MfaCodeRequest(code = code))

        val response = checkAuth(window.fetch("$baseUrl/api/mfa/disable", options).await())
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to disable MFA: $errorText")
        }
    }

    /**
     * Regenerate backup codes (requires valid TOTP code).
     */
    suspend fun regenerateMfaBackupCodes(code: String): List<String> {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(MfaCodeRequest(code = code))

        val response = checkAuth(window.fetch("$baseUrl/api/mfa/regenerate-backup-codes", options).await())
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to regenerate backup codes: $errorText")
        }

        val text = response.text().await()
        val responseData = json.decodeFromString<MfaBackupCodesResponse>(text)
        return responseData.backupCodes
    }
    
    suspend fun createLinkToken(daysRequested: Int? = null): LinkTokenResponse {
        val userId = authenticatedUserId ?: throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        // Build JSON manually to avoid serialization issues with Map<String, Any>
        val jsonBody = if (daysRequested != null) {
            """{"user_id":"$userId","days_requested":$daysRequested}"""
        } else {
            """{"user_id":"$userId"}"""
        }
        options.body = jsonBody
        
        val response = checkAuth(window.fetch("$baseUrl/api/plaid/link_token", options).await())
        
        if (!response.ok) {
            throw Exception("Failed to create link token: ${response.status} ${response.statusText}")
        }
        
        val text = response.text().await()
        return json.decodeFromString<LinkTokenResponse>(text)
    }
    
    suspend fun exchangePublicToken(publicToken: String, daysRequested: Int? = null, institutionId: String? = null, institutionName: String? = null): ExchangeTokenResponse {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(ExchangeTokenRequest(public_token = publicToken, user_id = authenticatedUserId!!, days_requested = daysRequested, institution_id = institutionId, institution_name = institutionName))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/exchange_token", options).await())

        if (!response.ok) {
            throw Exception("Failed to exchange token: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<ExchangeTokenResponse>(text)
    }
    
    suspend fun getTransactions(accessToken: String, startDate: String, endDate: String): TransactionsResponse {
        val request = TransactionsRequest(
            access_token = accessToken,
            start_date = startDate,
            end_date = endDate
        )
        
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(request)
        
        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions", options).await())
        
        if (!response.ok) {
            throw Exception("Failed to get transactions: ${response.statusText}")
        }
        
        val text = response.text().await()
        return json.decodeFromString<TransactionsResponse>(text)
    }
    
    
    suspend fun getTransactionsFromServer(forceRefresh: Boolean = false): TransactionsResponse {
        val url = if (forceRefresh) {
            "$baseUrl/api/plaid/transactions?force_refresh=true"
        } else {
            "$baseUrl/api/plaid/transactions"
        }
        console.log("Making GET request to $url")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch(url, options).await())
        console.log("Response status:", response.status, response.statusText)

        if (response.status.toInt() == 401) {
            // Need authentication - throw special exception
            throw Exception("AUTH_REQUIRED")
        }

        // Handle 202 (Plaid syncing) status
        if (response.status.toInt() == 202) {
            val text = response.text().await()
            throw PlaidSyncingException(text.ifEmpty { "Plaid is still syncing transactions" })
        }

        if (!response.ok) {
            // Try to read the error response body for Plaid error details
            val errorBody = try {
                response.text().await()
            } catch (e: Exception) {
                ""
            }
            console.log("Error response body:", errorBody)

            // Check if it contains Plaid error info
            if (errorBody.contains("ITEM_LOGIN_REQUIRED", ignoreCase = true)) {
                throw Exception("ITEM_LOGIN_REQUIRED: Your bank connection requires re-authentication. Please re-link your account.")
            }

            if (errorBody.contains("plaid_token_missing", ignoreCase = true) ||
                errorBody.contains("No Plaid access token", ignoreCase = true)) {
                throw Exception("No Plaid access token found for this user. Please link an account first.")
            }

            throw Exception("Failed to get transactions: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<TransactionsResponse>(text)
    }

    suspend fun refreshTransactions() {
        if (!isAuthenticated()) {
            console.log("⚠️ Cannot refresh transactions - not authenticated")
            throw Exception("AUTH_REQUIRED")
        }

        console.log("Refreshing transactions - clearing server cache")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/refresh", options).await())

        if (response.status.toInt() == 401) {
            throw Exception("AUTH_REQUIRED")
        }

        if (!response.ok) {
            throw Exception("Failed to refresh transactions: ${response.status} ${response.statusText}")
        }
    }

    suspend fun getSubscriptionStatus(): SubscriptionStatusData {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/subscribe/status", options).await())

        if (!response.ok) {
            throw Exception("Failed to get subscription status: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<SubscriptionStatusData>(text)
    }

    /**
     * Request a one-time Stripe Customer Portal URL. Used when the user taps
     * Manage Subscription on the web. Returns null if the user has no Stripe
     * customer (e.g., admin-set Pro test account).
     */
    suspend fun getStripePortalUrl(returnUrl: String): String? {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        val headers = authHeaders()
        js("headers['Content-Type'] = 'application/json'")
        options.headers = headers
        // Hand-rolled JSON to avoid serializer ceremony for a one-field body.
        val escapedUrl = returnUrl.replace("\\", "\\\\").replace("\"", "\\\"")
        options.body = "{\"return_url\":\"$escapedUrl\"}"
        val response = checkAuth(window.fetch("$baseUrl/api/subscribe/portal", options).await())
        if (response.status.toInt() == 404) return null
        if (!response.ok) throw Exception("Portal session failed: ${response.status}")
        val text = response.text().await()
        val regex = Regex("\"url\"\\s*:\\s*\"([^\"]+)\"")
        return regex.find(text)?.groupValues?.get(1)
    }

    suspend fun deleteAccount(): Boolean {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/account", options).await())
        return response.ok
    }

    suspend fun forgotPassword(email: String): Boolean {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(ForgotPasswordRequest(email = email))
        val response = window.fetch("$baseUrl/api/forgot-password", options).await()
        return response.ok
    }

    suspend fun resetPassword(token: String, newPassword: String): Boolean {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(ResetPasswordRequest(token = token, newPassword = newPassword))
        val response = window.fetch("$baseUrl/api/reset-password", options).await()
        return response.ok
    }

    suspend fun verifyEmail(token: String): Result<String> {
        val options = RequestInit()
        options.method = "GET"
        val response = window.fetch("$baseUrl/api/verify-email?token=$token", options).await()
        return if (response.ok) {
            Result.success("Email verified")
        } else {
            Result.failure(Exception("Verification failed"))
        }
    }

    suspend fun resendVerification(email: String): Result<String> {
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(ResendVerificationRequest(email = email))
        val response = window.fetch("$baseUrl/api/resend-verification", options).await()
        return if (response.ok) {
            Result.success("Verification email resent")
        } else {
            Result.failure(Exception("Failed to resend verification email"))
        }
    }

    suspend fun getPlaidLinkStatus(): PlaidLinkStatusResponse {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/link_status", options).await())

        if (!response.ok) {
            throw Exception("Failed to get Plaid link status: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<PlaidLinkStatusResponse>(text)
    }

    suspend fun getPlaidEnvironment(): String {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/environment", options).await())

        if (!response.ok) {
            throw Exception("Failed to get Plaid environment: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val envResponse = json.decodeFromString<PlaidEnvironmentResponse>(text)
        return envResponse.environment
    }

    suspend fun createSandboxUser(daysOfHistory: Int): CreateSandboxUserResponse {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(CreateSandboxUserRequest(days_of_history = daysOfHistory))

        console.log("Creating sandbox user with $daysOfHistory days of history...")
        val response = checkAuth(window.fetch("$baseUrl/api/plaid/sandbox_create_user", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            console.error("Failed to create sandbox user:", errorText)
            throw Exception("Failed to create sandbox user: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        console.log("Sandbox user created successfully!")
        return json.decodeFromString<CreateSandboxUserResponse>(text)
    }

    // MARK: - User Settings API

    suspend fun getAllSettings(): List<UserSettingResponse> {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/settings", options).await())

        if (!response.ok) {
            throw Exception("Failed to get settings: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val settingsResponse = json.decodeFromString<UserSettingsListResponse>(text)
        return settingsResponse.settings
    }

    suspend fun getSetting(key: String): String? {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/settings/$key", options).await())

        if (!response.ok) {
            throw Exception("Failed to get setting: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val settingResponse = json.decodeFromString<GetSettingResponse>(text)
        return settingResponse.value
    }

    suspend fun setSetting(key: String, value: String) {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(UserSettingRequest(key = key, value = value))

        val response = checkAuth(window.fetch("$baseUrl/api/settings", options).await())

        if (!response.ok) {
            throw Exception("Failed to save setting: ${response.status} ${response.statusText}")
        }
    }

    // MARK: - Goal Allocation Studio API

    /**
     * Fetches the user's Studio profile. Returns null when the server
     * responds 204 (no profile yet) — the caller renders the onboarding
     * stepper to collect one. Any other failure throws.
     */
    suspend fun getStudioProfile(): studio.Wiz3rUserProfile? {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/studio/profile", options).await())
        if (response.status == 204.toShort()) return null
        if (!response.ok) throw Exception("Failed to load Studio profile: ${response.status}")
        val text = response.text().await()
        return json.decodeFromString(studio.Wiz3rUserProfile.serializer(), text)
    }

    /**
     * Upserts the user's Studio profile. The server stores it as a JSON blob
     * under the user_settings key `wiz3r_user_profile_json` — no separate
     * schema. See StudioRoutes.kt on the server side.
     */
    suspend fun saveStudioProfile(profile: studio.Wiz3rUserProfile) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        val body = json.encodeToString(studio.Wiz3rUserProfile.serializer(), profile)
        console.log("🧪 saveStudioProfile body:", body)
        options.body = body
        val response = checkAuth(window.fetch("$baseUrl/api/studio/profile", options).await())
        if (!response.ok) throw Exception("Failed to save Studio profile: ${response.status}")
    }

    // MARK: - Budget Planning API

    suspend fun getBudgetPlansByYear(year: Int): List<BudgetPlanResponse> {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/budget/$year", options).await())

        if (!response.ok) {
            throw Exception("Failed to get budget plans: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val budgetResponse = json.decodeFromString<BudgetPlansResponse>(text)
        return budgetResponse.plans
    }

    suspend fun getBudgetPlansByMonth(year: Int, month: Int): List<BudgetPlanResponse> {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/budget/$year/$month", options).await())

        if (!response.ok) {
            throw Exception("Failed to get budget plans: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        val budgetResponse = json.decodeFromString<BudgetPlansResponse>(text)
        return budgetResponse.plans
    }

    suspend fun updateBudgetPlan(year: Int, month: Int, categoryType: String, categoryName: String, plannedAmount: Double) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(UpdateBudgetPlanRequest(year, month, categoryType, categoryName, plannedAmount))

        val response = checkAuth(window.fetch("$baseUrl/api/budget/$year/$month", options).await())

        if (!response.ok) {
            throw Exception("Failed to update budget plan: ${response.status} ${response.statusText}")
        }
    }

    /**
     * Clears every monthly budget entry for a single (categoryType, categoryName)
     * in the given year. Used by the primary↔sub source-of-truth transition.
     */
    suspend fun deleteBudgetPlanCategory(year: Int, categoryType: String, categoryName: String) {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }
        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()
        val encodedName = js("encodeURIComponent")(categoryName) as String
        val response = checkAuth(
            window.fetch("$baseUrl/api/budget/$year/category/$categoryType/$encodedName", options).await()
        )
        if (!response.ok) {
            throw Exception("Failed to delete budget category: ${response.status} ${response.statusText}")
        }
    }

    suspend fun copyBudgetPlan(fromYear: Int, toYear: Int) {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/budget/copy/$fromYear/$toYear", options).await())

        if (!response.ok) {
            throw Exception("Failed to copy budget plan: ${response.status} ${response.statusText}")
        }
    }

    // MARK: - Split/Distribute Transaction API

    suspend fun splitTransaction(transactionId: String, splits: List<SplitItemRequest>): SplitTransactionResponse {
        if (ReadOnlyGate.demoMode) return SplitTransactionResponse(true, emptyList(), "demo")
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(SplitTransactionRequestBody(splits = splits))

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/split", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to split transaction: $errorText")
        }

        val text = response.text().await()
        return json.decodeFromString<SplitTransactionResponse>(text)
    }

    suspend fun getChildTransactions(parentId: String): ChildTransactionsResponse {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$parentId/children", options).await())

        if (!response.ok) {
            throw Exception("Failed to get child transactions: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<ChildTransactionsResponse>(text)
    }

    suspend fun deleteSplit(transactionId: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/split", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to delete split: $errorText")
        }
    }

    suspend fun distributeTransaction(transactionId: String, startMonth: String, endMonth: String): SplitTransactionResponse {
        if (ReadOnlyGate.demoMode) return SplitTransactionResponse(true, emptyList(), "demo")
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(DistributeTransactionRequestBody(startMonth = startMonth, endMonth = endMonth))

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/distribute", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to distribute transaction: $errorText")
        }

        val text = response.text().await()
        return json.decodeFromString<SplitTransactionResponse>(text)
    }

    // MARK: - Transaction Category Update API

    suspend fun updateTransactionCategory(
        transactionId: String,
        category: String,
        applyToSimilar: Boolean = false,
        selectedTransactionIds: List<String>? = null,
    ) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "PATCH"
        options.headers = authHeaders()
        options.body = json.encodeToString(
            UpdateCategoryRequest(
                category = category,
                applyToSimilar = applyToSimilar,
                selectedTransactionIds = selectedTransactionIds,
            ),
        )

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/category", options).await())

        if (response.status.toInt() == 403) {
            val errorText = response.text().await()
            if (errorText.contains("subscription_required")) {
                throw Exception("SUBSCRIPTION_REQUIRED")
            }
            throw Exception("Failed to update category: $errorText")
        }
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to update category: $errorText")
        }
    }

    suspend fun getCategoryPreview(transactionId: String): CategoryPreviewResponse {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/category-preview", options).await())
        if (!response.ok) {
            throw Exception("Failed to load preview")
        }
        val text = response.text().await()
        return json.decodeFromString<CategoryPreviewResponse>(text)
    }

    // MARK: - Custom Category API

    suspend fun getCustomCategories(): List<CustomCategoryData> {
        if (!isAuthenticated()) return emptyList()
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/categories/custom", options).await())
        if (!response.ok) return emptyList()
        return json.decodeFromString(response.text().await())
    }

    suspend fun createCustomCategory(
        primaryName: String,
        secondaryName: String,
        isIncome: Boolean,
        icon: String? = null,
    ): CustomCategoryData {
        if (ReadOnlyGate.demoMode) return CustomCategoryData(id = kotlin.js.Date.now().toLong(), primaryName = primaryName, secondaryName = secondaryName, isIncome = isIncome, icon = icon)
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(
            CreateCustomCategoryRequest(primaryName = primaryName, secondaryName = secondaryName, isIncome = isIncome, icon = icon)
        )
        val response = checkAuth(window.fetch("$baseUrl/api/categories/custom", options).await())
        if (!response.ok) {
            // Surface the server's error message (e.g. duplicate-category 409) so the
            // UI can show something specific rather than a generic "failed".
            val body = runCatching { response.text().await() }.getOrNull().orEmpty()
            val msg = runCatching {
                val obj = kotlinx.serialization.json.Json.parseToJsonElement(body).jsonObject
                obj["error"]?.jsonPrimitive?.content
            }.getOrNull()
            throw Exception(msg ?: "Failed to create custom category")
        }
        return json.decodeFromString(response.text().await())
    }

    suspend fun updateCustomCategory(
        id: Long,
        primaryName: String? = null,
        secondaryName: String? = null,
        icon: String? = null,
    ): CustomCategoryData {
        if (ReadOnlyGate.demoMode) return CustomCategoryData(id = id, primaryName = primaryName ?: "", secondaryName = secondaryName ?: "", isIncome = false, icon = icon)
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(
            UpdateCustomCategoryRequest(primaryName = primaryName, secondaryName = secondaryName, icon = icon)
        )
        val response = checkAuth(window.fetch("$baseUrl/api/categories/custom/$id", options).await())
        if (!response.ok) throw Exception("Failed to update custom category")
        return json.decodeFromString(response.text().await())
    }

    suspend fun getCustomCategoryUsage(id: Long): CustomCategoryUsageResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/categories/custom/$id/usage", options).await())
        if (!response.ok) throw Exception("Failed to load usage")
        return json.decodeFromString(response.text().await())
    }

    suspend fun deleteCustomCategory(
        id: Long,
        reassignToPrimary: String? = null,
        reassignToSecondary: String? = null,
    ): DeleteCustomCategoryResponse {
        if (ReadOnlyGate.demoMode) return DeleteCustomCategoryResponse(deleted = true)
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()
        options.body = json.encodeToString(
            ReassignCustomCategoryRequest(toPrimaryName = reassignToPrimary, toSecondaryName = reassignToSecondary)
        )
        val response = checkAuth(window.fetch("$baseUrl/api/categories/custom/$id", options).await())
        if (!response.ok) throw Exception("Failed to delete custom category")
        return json.decodeFromString(response.text().await())
    }

    // MARK: - Transaction Notes API

    suspend fun updateTransactionNotes(transactionId: String, notes: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "PATCH"
        options.headers = authHeaders()
        options.body = json.encodeToString(UpdateNotesRequest(notes = notes))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/notes", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to update notes: $errorText")
        }
    }

    suspend fun markExcluded(transactionId: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "PATCH"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/exclude", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to exclude transaction: $errorText")
        }
    }

    suspend fun unmarkExcluded(transactionId: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/exclude", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to include transaction: $errorText")
        }
    }

    suspend fun resetTransactionCategory(transactionId: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/category", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to reset category: $errorText")
        }
    }

    /**
     * Verify a transaction's category (confirm AI/Plaid category without changing it).
     * Bumps confidence to 100% and propagates to similar transactions.
     */
    suspend fun verifyTransactionCategory(transactionId: String, propagate: Boolean = true): VerifyCategoryResponse {
        if (ReadOnlyGate.demoMode) return VerifyCategoryResponse("demo", "demo", 0)
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(VerifyCategoryRequest(propagate = propagate))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/$transactionId/verify", options).await())

        if (response.status.toInt() == 401) {
            throw Exception("AUTH_REQUIRED")
        }

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to verify category: $errorText")
        }

        val text = response.text().await()
        return json.decodeFromString<VerifyCategoryResponse>(text)
    }

    // MARK: - Goal Assignment API

    suspend fun assignTransactionToGoal(
        transactionId: String,
        goalId: String,
        amount: Double? = null,
        createRule: Boolean = false
    ) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(AssignGoalRequest(
            goal_id = goalId,
            amount = amount,
            create_rule = createRule
        ))

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/assign-goal", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to assign transaction to goal: $errorText")
        }
    }

    suspend fun removeGoalAssignment(transactionId: String) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/goal-assignment", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to remove goal assignment: $errorText")
        }
    }

    // MARK: - Subscription Detection API

    suspend fun detectSubscriptions(): DetectedSubscriptionsResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/subscriptions/detect", options).await())
        if (!response.ok) throw Exception("Failed to detect subscriptions")
        val text = response.text().await()
        return json.decodeFromString(text)
    }

    suspend fun updateSubscriptionStatus(merchantKey: String, status: String) {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        val body = js("{}")
        body.merchantKey = merchantKey
        body.status = status
        options.body = JSON.stringify(body)
        val response = checkAuth(window.fetch("$baseUrl/api/subscriptions/status", options).await())
        if (!response.ok) throw Exception("Failed to update subscription status")
    }

    // MARK: - Plaid Account Management API

    suspend fun getLinkedInstitutions(): List<LinkedInstitution> {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/linked_institutions", options).await())

        if (!response.ok) {
            throw Exception("Failed to get linked institutions: ${response.status} ${response.statusText}")
        }

        val text = response.text().await()
        return json.decodeFromString<List<LinkedInstitution>>(text)
    }

    suspend fun unlinkInstitution(itemId: String) {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val userId = Companion.getSharedUserId() ?: throw Exception("User not authenticated")

        val body = kotlinx.serialization.json.buildJsonObject {
            put("user_id", kotlinx.serialization.json.JsonPrimitive(userId))
            put("item_id", kotlinx.serialization.json.JsonPrimitive(itemId))
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(kotlinx.serialization.json.JsonObject.serializer(), body)

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/revoke_token", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to unlink institution: $errorText")
        }
    }

    suspend fun getRelinkToken(itemId: String): String {
        if (!isAuthenticated()) {
            throw Exception("User not authenticated")
        }

        val userId = Companion.getSharedUserId() ?: throw Exception("User not authenticated")

        val body = kotlinx.serialization.json.buildJsonObject {
            put("user_id", kotlinx.serialization.json.JsonPrimitive(userId))
            put("item_id", kotlinx.serialization.json.JsonPrimitive(itemId))
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(kotlinx.serialization.json.JsonObject.serializer(), body)

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/relink_token", options).await())

        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to get relink token: $errorText")
        }

        val text = response.text().await()
        val responseObj = json.decodeFromString<LinkTokenResponse>(text)
        return responseObj.link_token
    }

    suspend fun confirmRelink(itemId: String) {
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val body = kotlinx.serialization.json.buildJsonObject {
            put("item_id", kotlinx.serialization.json.JsonPrimitive(itemId))
        }

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(kotlinx.serialization.json.JsonObject.serializer(), body)

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/confirm_relink", options).await())
        if (!response.ok) {
            console.error("Failed to confirm relink: ${response.status}")
        }
    }

    // MARK: - Phase B merge: splice unlinked-account history into freshly re-linked accounts

    @Serializable
    data class MergeCandidate(
        val new_account_id: String,
        val new_account_name: String,
        val new_account_mask: String?,
        val old_account_id: String,
        val old_account_name: String,
        val old_account_mask: String?,
        val old_transaction_count: Int,
        val confidence: String, // "HIGH" | "MEDIUM" | "LOW"
    )

    @Serializable
    data class MergeCandidatesResponse(val candidates: List<MergeCandidate>)

    @Serializable
    data class MergeExecuteRequestPair(
        val new_account_id: String,
        val old_account_id: String,
    )

    @Serializable
    data class MergeExecuteRequest(val pairs: List<MergeExecuteRequestPair>)

    @Serializable
    data class MergeExecuteResult(
        val new_account_id: String,
        val old_account_id: String,
        val merged_transaction_count: Int,
        val reparented_transaction_count: Int,
        val dropped_account_id: String,
    )

    @Serializable
    data class MergeExecuteResponse(val results: List<MergeExecuteResult>)

    suspend fun getMergeCandidates(itemId: String): MergeCandidatesResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(
            window.fetch("$baseUrl/api/plaid/merge/candidates?item_id=${js("encodeURIComponent")(itemId)}", options).await()
        )
        if (!response.ok) throw Exception("Failed to get merge candidates: ${response.status}")
        val text = response.text().await()
        return json.decodeFromString(MergeCandidatesResponse.serializer(), text)
    }

    @Serializable
    data class InstitutionStatusBreakdown(
        val success: Double? = null,
        val error_plaid: Double? = null,
        val error_institution: Double? = null,
        val refresh_interval: String? = null,
    )

    @Serializable
    data class InstitutionProductStatus(
        val status: String, // HEALTHY | DEGRADED | DOWN
        val last_status_change: String? = null,
        val breakdown: InstitutionStatusBreakdown? = null,
    )

    @Serializable
    data class InstitutionHealth(
        val item_logins: InstitutionProductStatus? = null,
        val transactions_updates: InstitutionProductStatus? = null,
        val auth: InstitutionProductStatus? = null,
    )

    @Serializable
    data class InstitutionWithStatus(
        val institution_id: String,
        val name: String,
        val country_codes: List<String> = emptyList(),
        val products: List<String> = emptyList(),
        val status: InstitutionHealth? = null,
    )

    @Serializable
    data class InstitutionStatusResponse(
        val institution: InstitutionWithStatus,
        val request_id: String? = null,
    )

    /**
     * Fetch institution health by either institution_id OR item_id (server
     * resolves institution_id from the user's plaid_tokens). Returns null on
     * failure — callers should treat unknown status as "no warning".
     */
    suspend fun getInstitutionStatusByItem(itemId: String): InstitutionStatusResponse? {
        if (!isAuthenticated()) return null
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        return try {
            val response = checkAuth(
                window.fetch("$baseUrl/api/plaid/institution_status?item_id=${js("encodeURIComponent")(itemId)}", options).await()
            )
            if (!response.ok) return null
            json.decodeFromString(InstitutionStatusResponse.serializer(), response.text().await())
        } catch (e: Exception) {
            console.warn("getInstitutionStatusByItem failed (non-fatal):", e.message)
            null
        }
    }

    suspend fun executeMerge(pairs: List<MergeExecuteRequestPair>): MergeExecuteResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(MergeExecuteRequest.serializer(), MergeExecuteRequest(pairs = pairs))
        val response = checkAuth(window.fetch("$baseUrl/api/plaid/merge/execute", options).await())
        if (!response.ok) throw Exception("Failed to execute merge: ${response.status}")
        val text = response.text().await()
        return json.decodeFromString(MergeExecuteResponse.serializer(), text)
    }

    // MARK: - Statement Accounts (Manual) API

    @Serializable
    data class ManualAccountInfo(
        val accountId: String,
        val institutionName: String,
        val accountName: String,
        val accountType: String,
        val balance: Double,
        val transactionCount: Int,
    )

    suspend fun getManualAccounts(): List<ManualAccountInfo> {
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/accounts/manual", options).await())
        if (!response.ok) throw Exception("Failed to get manual accounts: ${response.status}")

        val text = response.text().await()
        return json.decodeFromString<List<ManualAccountInfo>>(text)
    }

    suspend fun deleteManualAccount(accountId: String) {
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()

        val response = checkAuth(window.fetch("$baseUrl/api/accounts/$accountId", options).await())
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to delete account: $errorText")
        }
    }

    // MARK: - Batch Operations API

    suspend fun batchUpdateCategory(transactionIds: List<String>, category: String): BatchOperationResponse {
        if (ReadOnlyGate.demoMode) return BatchOperationResponse("demo", transactionIds.size, 0)
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchCategoryRequest(transactionIds, category))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/batch/category", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch update category: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchOperationResponse>(text)
    }

    suspend fun batchAssignGoal(transactionIds: List<String>, goalId: String): BatchOperationResponse {
        if (ReadOnlyGate.demoMode) return BatchOperationResponse("demo", transactionIds.size, 0)
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchAssignGoalRequest(transactionIds, goalId))

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/batch/assign-goal", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch assign goal: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchOperationResponse>(text)
    }

    suspend fun batchExclude(transactionIds: List<String>): BatchOperationResponse {
        if (ReadOnlyGate.demoMode) return BatchOperationResponse("demo", transactionIds.size, 0)
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchExcludeRequest(transactionIds))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/batch/exclude", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch exclude: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchOperationResponse>(text)
    }

    suspend fun batchRemoveGoal(transactionIds: List<String>): BatchOperationResponse {
        if (ReadOnlyGate.demoMode) return BatchOperationResponse("demo", transactionIds.size, 0)
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchExcludeRequest(transactionIds))

        val response = checkAuth(window.fetch("$baseUrl/api/transactions/batch/remove-goal", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch remove goal: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchOperationResponse>(text)
    }

    suspend fun batchInclude(transactionIds: List<String>): BatchOperationResponse {
        if (ReadOnlyGate.demoMode) return BatchOperationResponse("demo", transactionIds.size, 0)
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchExcludeRequest(transactionIds))

        val response = checkAuth(window.fetch("$baseUrl/api/plaid/transactions/batch/include", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch include: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchOperationResponse>(text)
    }

    // MARK: - Context API

    suspend fun getContexts(): ContextListResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "GET"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/contexts", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) throw Exception("Failed to fetch contexts: ${response.text().await()}")
        val text = response.text().await()
        return json.decodeFromString<ContextListResponse>(text)
    }

    suspend fun createContext(name: String): ContextApiModel {
        if (ReadOnlyGate.demoMode) return ContextApiModel(id = kotlin.js.Date.now().toString(), user_id = "demo", name = name, color = "#00D9FF")
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(CreateContextRequest.serializer(), CreateContextRequest(name = name))
        val response = checkAuth(window.fetch("$baseUrl/api/contexts", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) throw Exception("Failed to create context: ${response.text().await()}")
        val text = response.text().await()
        return json.decodeFromString<ContextApiModel>(text)
    }

    suspend fun updateContext(contextId: String, name: String, color: String? = null): ContextApiModel {
        if (ReadOnlyGate.demoMode) return ContextApiModel(id = contextId, user_id = "demo", name = name, color = color ?: "#00D9FF")
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(UpdateContextRequest.serializer(), UpdateContextRequest(name = name, color = color))
        val response = checkAuth(window.fetch("$baseUrl/api/contexts/$contextId", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) throw Exception("Failed to update context: ${response.text().await()}")
        val text = response.text().await()
        return json.decodeFromString<ContextApiModel>(text)
    }

    suspend fun deleteContext(contextId: String): DeleteContextResponse {
        if (ReadOnlyGate.demoMode) return DeleteContextResponse(0)
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "DELETE"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/contexts/$contextId", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) throw Exception("Failed to delete context: ${response.text().await()}")
        val text = response.text().await()
        return json.decodeFromString<DeleteContextResponse>(text)
    }

    suspend fun setTransactionContexts(transactionId: String, contextIds: List<String>) {
        if (ReadOnlyGate.demoMode) return
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(SetTransactionContextsRequest.serializer(), SetTransactionContextsRequest(contextIds))
        val response = checkAuth(window.fetch("$baseUrl/api/transactions/$transactionId/contexts", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) throw Exception("Failed to set transaction contexts: ${response.text().await()}")
    }

    suspend fun batchAssignContexts(transactionIds: List<String>, contextIds: List<String>): BatchAssignResponse {
        if (ReadOnlyGate.demoMode) return BatchAssignResponse(transactionIds.size)
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "PUT"
        options.headers = authHeaders()
        options.body = json.encodeToString(BatchAssignContextsRequest.serializer(), BatchAssignContextsRequest(transactionIds, contextIds))
        val response = checkAuth(window.fetch("$baseUrl/api/transactions/batch/contexts", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to batch assign contexts: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<BatchAssignResponse>(text)
    }

    // MARK: - Notification Phone Verification API

    suspend fun verifyPhone(phoneNumber: String): PhoneVerificationResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(VerifyPhoneRequest(phone_number = phoneNumber))

        val response = checkAuth(window.fetch("$baseUrl/api/notifications/verify-phone", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Failed to send verification code: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<PhoneVerificationResponse>(text)
    }

    suspend fun confirmPhone(phoneNumber: String, code: String): PhoneConfirmResponse {
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        options.body = json.encodeToString(ConfirmPhoneRequest(phone_number = phoneNumber, code = code))

        val response = checkAuth(window.fetch("$baseUrl/api/notifications/confirm-phone", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        if (!response.ok) {
            val errorText = response.text().await()
            throw Exception("Phone verification failed: $errorText")
        }
        val text = response.text().await()
        return json.decodeFromString<PhoneConfirmResponse>(text)
    }

    /** Force-fire a notification end-to-end for the current user (parity with iOS). */
    suspend fun fireTestNotification(reset: Int = 3): TestFireResult {
        if (!isAuthenticated()) throw Exception("User not authenticated")
        val options = RequestInit()
        options.method = "POST"
        options.headers = authHeaders()
        val response = checkAuth(window.fetch("$baseUrl/api/notifications/test-fire?reset=$reset", options).await())
        if (response.status.toInt() == 401) throw Exception("AUTH_REQUIRED")
        val text = response.text().await()
        if (!response.ok) {
            // Server returns a TestFireResponse-shaped body even on 4xx, but
            // surface a plain error if not parseable.
            return runCatching { json.decodeFromString<TestFireResult>(text) }
                .getOrElse { TestFireResult(sent = false, hint = "Failed to fire test notification: $text") }
        }
        return json.decodeFromString<TestFireResult>(text)
    }

    // MARK: - Account Custom Name API

    /**
     * Renames a Plaid account (or clears its custom name when [customName] is blank/null).
     * Returns the stored name (null when cleared). Throws [AccountNameError] on 400/403/404/409
     * with the server's stable `error` code so the call site can localize the message.
     */
    suspend fun updateAccountCustomName(accountId: String, customName: String?): String? {
        if (ReadOnlyGate.demoMode) return customName
        if (!isAuthenticated()) throw Exception("User not authenticated")

        val options = RequestInit()
        options.method = "PATCH"
        options.headers = authHeaders()
        options.body = json.encodeToString(UpdateAccountNameRequest(custom_name = customName))

        val response = checkAuth(window.fetch("$baseUrl/api/accounts/$accountId/custom-name", options).await())
        val body = response.text().await()
        if (!response.ok) {
            val error = json.decodeFromString<ApiErrorResponse>(body)
            throw AccountNameError(error.error, error.message)
        }
        return json.decodeFromString<UpdateAccountNameResponse>(body).custom_name
    }
}

@Serializable
data class TestFireResult(
    val sent: Boolean,
    val resetCount: Int = 0,
    val userId: String = "",
    val channel: String = "",
    val hint: String? = null,
)

// Batch Operation API Models
@Serializable
data class BatchCategoryRequest(val transactionIds: List<String>, val category: String)
@Serializable
data class BatchExcludeRequest(val transactionIds: List<String>)
@Serializable
data class BatchAssignGoalRequest(val transactionIds: List<String>, val goalId: String)
@Serializable
data class BatchOperationResponse(val status: String, val successCount: Int, val failedCount: Int, val failures: List<BatchFailure> = emptyList())
@Serializable
data class BatchFailure(val transactionId: String, val reason: String)

// Category Update API Models
@Serializable
data class UpdateCategoryRequest(
    val category: String,
    val applyToSimilar: Boolean = false,
    val selectedTransactionIds: List<String>? = null,
)

@Serializable
data class CategoryPreviewItem(
    val transactionId: String,
    val name: String,
    val date: String,
    val amount: Double,
    val detailedCategory: String? = null,
    val preselected: Boolean = false,
)

@Serializable
data class CategoryPreviewResponse(
    val sourceTransaction: CategoryPreviewItem,
    val affectedTransactions: List<CategoryPreviewItem>,
    val count: Int,
)

@Serializable
data class UpdateNotesRequest(
    val notes: String
)

@Serializable
data class VerifyCategoryRequest(
    val propagate: Boolean = true
)

// Goal Assignment API Models
@Serializable
data class AssignGoalRequest(
    val goal_id: String,
    val amount: Double? = null,
    val create_rule: Boolean = false
)

// Budget API Models
@Serializable
data class BudgetPlanResponse(
    val id: String,
    val year: Int,
    val month: Int,
    val categoryType: String,
    val categoryName: String,
    val plannedAmount: Double
)

@Serializable
data class BudgetPlansResponse(
    val plans: List<BudgetPlanResponse>
)

@Serializable
data class UpdateBudgetPlanRequest(
    val year: Int,
    val month: Int,
    val categoryType: String,
    val categoryName: String,
    val plannedAmount: Double
)

@Serializable
data class PlaidEnvironmentResponse(
    val environment: String
)

@Serializable
data class CreateSandboxUserRequest(
    val days_of_history: Int
)

@Serializable
data class CreateSandboxUserResponse(
    val public_token: String,
    val transaction_count: Int
)

class PlaidSyncingException(message: String) : Exception(message)

// MFA API models
@Serializable
data class MfaRequiredResponse(
    val requiresMfa: Boolean = true,
    val tempToken: String,
    val userId: String,
    val message: String = "MFA verification required"
)

@Serializable
data class MfaStatusResponse(
    val mfaEnabled: Boolean,
    val mfaAvailable: Boolean
)

@Serializable
data class MfaSetupResponse(
    val secret: String,
    val otpAuthUri: String,
    val backupCodes: List<String>
)

@Serializable
data class MfaVerifyRequest(
    val code: String,
    val backupCodes: List<String>? = null
)

@Serializable
data class MfaCodeRequest(
    val code: String
)

@Serializable
data class MfaLoginVerifyRequest(
    val tempToken: String,
    val code: String
)

@Serializable
data class MfaBackupCodesResponse(
    val backupCodes: List<String>
)

class MfaRequiredException(
    val tempToken: String,
    val userId: String
) : Exception("MFA verification required")

class SessionExpiredException(message: String) : Exception(message)

// Split/Distribute Transaction API models
@Serializable
data class SplitItemRequest(
    val amount: Double,
    val category: String,
    val detailedCategory: String? = null,
    val note: String? = null
)

@Serializable
data class SplitTransactionRequestBody(
    val splits: List<SplitItemRequest>
)

@Serializable
data class DistributeTransactionRequestBody(
    val startMonth: String,
    val endMonth: String
)

@Serializable
data class SplitTransactionResponse(
    val success: Boolean,
    val childTransactionIds: List<String>,
    val message: String? = null
)

@Serializable
data class ChildTransactionData(
    val transactionId: String,
    val amount: Double,
    val name: String,
    val date: String,
    val category: String? = null,
    val detailedCategory: String? = null,
    val childType: String? = null,
    val splitOrder: Long? = null,
    val distributionMonthIndex: Int? = null,
    val distributionTotalMonths: Int? = null,
)

@Serializable
data class ChildTransactionsResponse(
    val parentTransactionId: String,
    val children: List<ChildTransactionData>
)

@Serializable
data class CustomCategoryData(
    val id: Long,
    val primaryName: String,
    val secondaryName: String,
    val isIncome: Boolean,
    val icon: String? = null,
)

/**
 * Find the icon id a user picked for a (primary, sub) pair in their custom categories.
 * When [subName] is null/blank, falls back to the first custom sub under [primaryName]
 * (that becomes the primary's visual identity — no separate "primary icon" storage).
 * Returns null if no custom row matches or the matched row has no icon set. This helper
 * is the single source of truth used by every icon-render site in the web client.
 */
fun findCustomIconId(
    customs: List<CustomCategoryData>,
    primaryName: String,
    subName: String? = null,
): String? {
    if (primaryName.isEmpty()) return null
    val match = if (!subName.isNullOrBlank()) {
        customs.firstOrNull {
            it.primaryName.equals(primaryName, ignoreCase = true) &&
                it.secondaryName.equals(subName, ignoreCase = true)
        }
    } else {
        customs.firstOrNull { it.primaryName.equals(primaryName, ignoreCase = true) }
    }
    return match?.icon?.takeIf { it.isNotBlank() }
}

@Serializable
data class UpdateCustomCategoryRequest(
    val primaryName: String? = null,
    val secondaryName: String? = null,
    val icon: String? = null,
)

@Serializable
data class CustomCategoryUsageSample(
    val id: String,
    val merchant: String,
    val amount: Double,
    val date: String,
)

@Serializable
data class CustomCategoryUsageResponse(
    val count: Long,
    val sample: List<CustomCategoryUsageSample> = emptyList(),
)

@Serializable
data class ReassignCustomCategoryRequest(
    val toPrimaryName: String? = null,
    val toSecondaryName: String? = null,
)

@Serializable
data class DeleteCustomCategoryResponse(
    val deleted: Boolean,
    val updates: Map<String, String> = emptyMap(),
)

@Serializable
data class ExcludeTransactionRequest(
    val excluded: Boolean = true,
)

@Serializable
data class CreateCustomCategoryRequest(
    val primaryName: String,
    val secondaryName: String,
    val isIncome: Boolean,
    val icon: String? = null,
)

// Subscription detection models
@Serializable
data class DetectedSubscription(
    val merchantKey: String,
    val displayName: String,
    val typicalAmount: Double,
    val occurrences: Int,
    val status: String,
    val averageIntervalDays: Int,
    val lastDate: String,
)

@Serializable
data class DetectedSubscriptionsResponse(
    val subscriptions: List<DetectedSubscription>,
    val confirmedMonthlyTotal: Double,
)

// Notification Phone Verification Models
@Serializable
data class VerifyPhoneRequest(val phone_number: String)

@Serializable
data class PhoneVerificationResponse(val status: String, val message: String? = null)

@Serializable
data class ConfirmPhoneRequest(val phone_number: String, val code: String)

// Account Custom Name Models
@Serializable
data class UpdateAccountNameRequest(val custom_name: String? = null)

@Serializable
data class UpdateAccountNameResponse(val account_id: String, val custom_name: String?)

/** Generic `{error, message}` shape returned by server error responses. */
@Serializable
data class ApiErrorResponse(val error: String, val message: String)

/**
 * Thrown by [ApiService.updateAccountCustomName] on a non-2xx response. Carries the server's
 * stable `error` code (e.g. "duplicate_name", "name_too_long") so callers can localize the
 * message via StringKey, falling back to [serverMessage] for unmapped codes.
 */
class AccountNameError(val code: String, val serverMessage: String) : Exception(serverMessage)

@Serializable
data class PhoneConfirmResponse(val status: String, val verified: Boolean = false, val message: String? = null)
